type BrowserStorageName = 'localStorage' | 'sessionStorage'

/**
 * Resolves browser storage without throwing during server rendering or when
 * browser privacy settings block storage access.
 *
 * This is intentionally imperative instead of using a React storage hook.
 * Tracking and persistence modules call it outside React components, and they
 * need immediate one-shot access without hook lifecycle or state updates.
 */
const browserStorageGet = (
  storageName: BrowserStorageName,
): Storage | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    return window[storageName]
  } catch {
    return undefined
  }
}

/**
 * Reads one browser-storage value and returns `null` when the storage or item
 * is unavailable, ensuring storage failures cannot interrupt the user flow.
 */
export const browserStorageReadItem = (
  storageName: BrowserStorageName,
  key: string,
): string | null => {
  const storage = browserStorageGet(storageName)

  if (!storage) {
    return null
  }

  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Writes one browser-storage value and silently skips the write when storage
 * is unavailable, ensuring persistence remains a non-blocking side effect.
 */
export const browserStorageWriteItem = (
  storageName: BrowserStorageName,
  key: string,
  value: string,
): void => {
  const storage = browserStorageGet(storageName)

  if (!storage) {
    return
  }

  try {
    storage.setItem(key, value)
  } catch {
    // Browser storage must never interrupt the user flow.
  }
}
